// src/app/core/auth/session.service.ts
import type { OnDestroy } from '@angular/core';
import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Subscription } from 'rxjs';
import { Subject, fromEvent, merge, interval } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';
import { AuthService } from './auth.service';
import type { ClockSample, SessionState } from './session-evaluation';
import { evaluateSession, resolveElapsedMs } from './session-evaluation';

const BROADCAST_CHANNEL_NAME       = 'bel_session';
const CORRELATION_STORAGE_KEY      = 'bel_session_correlation_id';
const LAST_ACTIVITY_STORAGE_KEY    = 'bel_session_last_activity_wall_ms';
const ACTIVITY_LOCAL_THROTTLE_MS   = 1_000;  // mousemove, etc. — cuánto trabajo genera D5
const ACTIVITY_BROADCAST_THROTTLE_MS = 15_000; // heartbeat entre pestañas — D4, §1.3
const TICK_INTERVAL_MS             = 1_000;

type LogoutReason = 'manual' | 'inactivity';

interface SessionBroadcastMessage {
  readonly type: 'ACTIVITY' | 'WARNING_DISMISSED' | 'LOGOUT';
  readonly correlationId: string;
  /** Solo presente en LOGOUT — qué mensaje (si alguno) debe ver el usuario en /login (§2.3). */
  readonly reason?: LogoutReason;
}

/**
 * SessionService — control de inactividad medido por reloj de pared.
 *
 * Reemplaza la cadena `merge(fromEvent(...)).pipe(switchMap(() =>
 * timer(...)))` del diseño original (Prompt 1) por dos mecanismos simples:
 * un único tick periódico que reevalúa `evaluateSession()` contra
 * Date.now()/performance.now(), y una reevaluación forzada en los puntos
 * donde el tiempo puede haber transcurrido sin que ningún timer lo notara
 * (pestaña oculta, bfcache, equipo suspendido — D3/D6/D8).
 *
 * `evaluateSession()` y `resolveElapsedMs()` (session-evaluation.ts) son la
 * única lógica de decisión; este servicio es fontanería alrededor de ellas.
 */
@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly config = inject(ConfigService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  // ── Identidad de la sesión ───────────────────────────────────────────────
  // A diferencia del Prompt 1 (un correlationId aleatorio por instancia de
  // pestaña), este ID ahora se restaura desde sessionStorage cuando existe:
  // es lo que permite que el guard de correlationId del BroadcastChannel
  // (§1.3) tenga sentido — una pestaña duplicada o recargada hereda el
  // mismo ID que el resto de pestañas de ESTA sesión, mientras que una
  // sesión distinta (login posterior) obtiene uno nuevo. Sigue sirviendo
  // también como X-Correlation-ID para logs del servidor (AuthInterceptor).
  readonly correlationId = signal<string>(this.loadOrGenerateCorrelationId());

  // ── Estado público ────────────────────────────────────────────────────────
  private readonly _state = signal<SessionState>('active');
  readonly state = this._state.asReadonly();

  private readonly _secondsRemaining = signal<number>(0);
  /** Segundos restantes hasta el cierre forzado — alimenta la cuenta atrás del modal. */
  readonly secondsRemaining = this._secondsRemaining.asReadonly();

  /**
   * @deprecated Compatibilidad con el modal de la Fase 0 (Prompt 1), que lee
   * `showWarning()` directamente. Se reemplaza por `state() === 'warning'`
   * en la Fase 2 — no se toca el modal todavía para no mezclar el rediseño
   * del servicio con cambios de UI en la misma fase.
   */
  readonly showWarning = computed(() => this._state() === 'warning');

  private readonly logoutSubject = new Subject<void>();
  /** Emite al cerrar sesión — usado por sessionCancelInterceptor para cortar peticiones en vuelo (D9, §1.5). */
  readonly loggedOut$ = this.logoutSubject.asObservable();

  private channel = this.createChannel();
  // Ver onPageHide/onPageShow: se cierra al salir de la pestaña para no
  // bloquear la elegibilidad de bfcache (defecto real D8/P12, ver abajo) y
  // se reabre si la página vuelve restaurada.
  private channelClosedForBfcache = false;

  private activitySub?: Subscription;
  private tickSub?: Subscription;
  private isTimerActive = false;

  private timeoutMs = 0;
  private warningMs = 0;

  // Reloj de pared y monótono de la última actividad conocida (§1.2, §1.2.1).
  private lastActivityWallMs = 0;
  private lastActivityMonoMs = 0;
  private lastActivityBroadcastAtMs = 0;

  private createChannel(): BroadcastChannel {
    const ch = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    ch.onmessage = (event: MessageEvent<SessionBroadcastMessage>) => {
      this.handleBroadcastMessage(event.data);
    };
    return ch;
  }

  /**
   * Inicia el control de inactividad. Se llama tanto tras un login
   * interactivo (LoginComponent) como al arrancar la app con una sesión ya
   * restaurada desde sessionStorage (AppComponent) — este segundo punto es
   * el que faltaba en el diseño original: sin él, recargar la página con
   * una sesión válida dejaba el control de inactividad permanentemente
   * apagado hasta el siguiente login manual.
   *
   * D8/P12, segunda vuelta: cerrar el BroadcastChannel en pagehide y quitar
   * "no-store" de Nginx (ver historial de commits) reducen cuánto bloquea
   * bfcache la propia app, pero no lo garantizan — hay navegadores/perfiles
   * donde la página nunca queda en bfcache pase lo que pase, y "Atrás" cae
   * en una recarga completa normal. Por eso NO se puede confiar solo en
   * pageshow+persisted: si la navegación de ESTA carga es de tipo
   * "back_forward" (Navigation Timing API — verificable incluso en una
   * recarga completa, a diferencia de performance.now(), que no sirve de
   * nada entre documentos distintos), se restaura lastActivityAt desde
   * sessionStorage en vez de reiniciarlo a "ahora". Una recarga real (F5,
   * tipo "reload") sigue reiniciando el contador sin cambios — ese es el
   * comportamiento correcto y ya verificado de P11.
   */
  startTimer(): void {
    this.stopTimer();

    const cfg = this.config.get('session');
    this.warningMs = cfg.warningMinutes * 60 * 1000;
    this.timeoutMs = cfg.timeoutMinutes * 60 * 1000;

    if (!this.tryRestoreActivityFromBackForwardNavigation()) {
      this.setLastActivityNow();
    }
    this.lastActivityBroadcastAtMs = 0;
    this.isTimerActive = true;

    // Reevaluar de inmediato: si la actividad restaurada arriba ya está
    // vencida, logout() debe disparar aquí — antes de suscribir nada más —
    // no esperar al primer tick.
    this.reevaluate();
    if (this._state() === 'expired') {
      return;
    }

    const activity$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'touchmove'),
      fromEvent(document, 'scroll', { passive: true }),
      fromEvent(document, 'wheel', { passive: true }),
    );

    this.activitySub = activity$.pipe(
      throttleTime(ACTIVITY_LOCAL_THROTTLE_MS, undefined, { leading: true, trailing: true }),
    ).subscribe(() => this.recordLocalActivity());

    this.tickSub = interval(TICK_INTERVAL_MS).subscribe(() => this.reevaluate());

    this.registerForcedReevaluationListeners();
  }

  /**
   * true si esta carga de página es una navegación Atrás/Adelante (según la
   * Navigation Timing API) Y hay una marca de actividad previa en
   * sessionStorage — en ese caso, restaura lastActivityWallMs desde ahí en
   * vez de reiniciar el contador. lastActivityMonoMs se fija al
   * performance.now() actual: el monótono de un documento anterior no
   * significa nada en este (cada documento tiene su propio origen de
   * tiempo), así que resolveElapsedMs() queda dependiendo del delta de
   * pared — que es exactamente el que sí sobrevive entre documentos.
   */
  private tryRestoreActivityFromBackForwardNavigation(): boolean {
    try {
      const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (entry?.type !== 'back_forward') return false;

      const stored = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
      if (!stored) return false;

      const restoredWallMs = Number(stored);
      if (!Number.isFinite(restoredWallMs)) return false;

      this.lastActivityWallMs = restoredWallMs;
      this.lastActivityMonoMs = performance.now();
      return true;
    } catch {
      return false; // Navigation Timing/sessionStorage inaccesibles — seguir con el comportamiento normal
    }
  }

  /** Detiene el mecanismo sin cerrar sesión. Llamado por startTimer() antes de reconfigurar y por terminateSession(). */
  stopTimer(): void {
    this.activitySub?.unsubscribe();
    this.activitySub = undefined;
    this.tickSub?.unsubscribe();
    this.tickSub = undefined;
    this.unregisterForcedReevaluationListeners();
    this.isTimerActive = false;

    // La expiración es terminal (§1.3, regla #15) — detener el mecanismo no
    // debe "revivir" una sesión ya expirada a estado 'active'.
    if (this._state() !== 'expired') {
      this._state.set('active');
    }
  }

  /** El usuario hizo clic en "Continuar sesión" en el modal. */
  renewSession(): void {
    if (!this.isTimerActive || this._state() === 'expired') return;
    this.setLastActivityNow();
    this.reevaluate();
    this.postMessage('WARNING_DISMISSED');
  }

  /**
   * Cierra la sesión y notifica a todas las pestañas abiertas.
   * Orden deliberado (§1.5): cortar el tráfico en curso primero, marcar el
   * estado como cerrado localmente, y solo entonces navegar — así cualquier
   * respuesta que llegue tarde encuentra el estado ya limpio.
   *
   * `reason` decide si /login muestra "su sesión se cerró por inactividad"
   * (§2.3, decisión 4 de 0.6). Un logout manual (botón del topbar, "Cerrar
   * sesión ahora" del modal) es una acción que el usuario acaba de elegir —
   * mostrarle ese mensaje sería confuso, no informativo. Se propaga por
   * BroadcastChannel para que las demás pestañas muestren el mismo mensaje
   * (o ninguno) que corresponde a la causa real del cierre.
   */
  logout(reason: LogoutReason = 'manual'): void {
    this.postMessage('LOGOUT', reason);
    this.terminateSession();
    this.navigateToLogin(reason);
  }

  /**
   * Genera un nuevo correlationId para esta sesión y lo persiste en
   * sessionStorage — así una pestaña duplicada o recargada durante la misma
   * sesión hereda el mismo ID (necesario para el guard de §1.3), mientras
   * que una sesión nueva (próximo login) obtiene uno distinto.
   */
  generateNewCorrelationId(): void {
    const id = this.generateCorrelationId();
    this.correlationId.set(id);
    this.trySetCorrelationStorage(id);
  }

  // ── Reevaluación y actividad ─────────────────────────────────────────────

  private recordLocalActivity(): void {
    if (!this.isTimerActive || this._state() === 'expired') return;
    this.setLastActivityNow();
    this.reevaluate();
    this.maybeBroadcastActivity();
  }

  private setLastActivityNow(): void {
    this.lastActivityWallMs = Date.now();
    this.lastActivityMonoMs = performance.now();
    this.persistLastActivityWall();
  }

  /**
   * Persiste lastActivityWallMs en sessionStorage — es lo que
   * tryRestoreActivityFromBackForwardNavigation() usa para detectar
   * inactividad real incluso cuando el navegador no usa bfcache y "Atrás"
   * termina siendo una recarga completa (D8/P12). No es una excepción a R14
   * (esa regla prohíbe localStorage, no sessionStorage — ya usado para
   * bel_session_user y bel_session_correlation_id).
   */
  private persistLastActivityWall(): void {
    try {
      sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(this.lastActivityWallMs));
    } catch {
      // No crítico: en el peor caso, una recarga tras Atrás/Adelante sin
      // bfcache se comporta como una recarga normal (reinicia el contador).
    }
  }

  /**
   * Único punto que decide el estado de la sesión. Se llama desde el tick
   * periódico Y desde cada reevaluación forzada (visibilitychange, focus,
   * online, pageshow) — nunca desde una cadena de temporizadores por evento.
   */
  private reevaluate(): void {
    if (!this.isTimerActive || this._state() === 'expired') return;

    const now: ClockSample = { wallMs: Date.now(), monoMs: performance.now() };
    const lastActivity: ClockSample = { wallMs: this.lastActivityWallMs, monoMs: this.lastActivityMonoMs };
    const elapsedMs = resolveElapsedMs(now, lastActivity);

    const next = evaluateSession(elapsedMs, 0, this.timeoutMs, this.warningMs);
    this._state.set(next);
    this._secondsRemaining.set(Math.max(0, Math.ceil((this.timeoutMs - elapsedMs) / 1000)));

    if (next === 'expired') {
      this.logout('inactivity');
    }
  }

  private maybeBroadcastActivity(): void {
    const nowMs = Date.now();
    if (nowMs - this.lastActivityBroadcastAtMs < ACTIVITY_BROADCAST_THROTTLE_MS) return;
    this.lastActivityBroadcastAtMs = nowMs;
    this.postMessage('ACTIVITY');
  }

  // ── Reevaluación forzada (D3, D8) ────────────────────────────────────────
  // Volver a la pestaña NUNCA cuenta como actividad — solo como el momento
  // de comprobar si la sesión ya había expirado mientras nadie miraba.

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.reevaluate();
  };

  private readonly onFocus = (): void => this.reevaluate();

  private readonly onOnline = (): void => this.reevaluate();

  /**
   * Un BroadcastChannel abierto es, en varios navegadores, motivo suficiente
   * para que la página quede fuera de la elegibilidad de bfcache — en cuyo
   * caso Atrás/Adelante no restaura nada: dispara una recarga completa
   * normal. Eso es lo que P12 detectó: con el canal siempre abierto, volver
   * con Atrás recargaba la app desde cero, y AppComponent.ngOnInit() (Fase
   * 1) arranca el temporizador fresco al detectar una sesión autenticada —
   * exactamente el comportamiento correcto para una recarga real (P11), pero
   * equivocado aquí, porque el usuario no recargó: solo volvió. Cerrar el
   * canal al salir le devuelve al navegador la posibilidad real de usar
   * bfcache, para que pageshow con persisted:true sí pueda ocurrir.
   */
  private readonly onPageHide = (): void => {
    this.channel.close();
    this.channelClosedForBfcache = true;
  };

  private readonly onPageShow = (_event: PageTransitionEvent): void => {
    // Reabrir el canal ANTES de reevaluar: si la sesión resulta expirada,
    // reevaluate() -> logout() necesita poder emitir LOGOUT a otras pestañas.
    if (this.channelClosedForBfcache) {
      this.channel = this.createChannel();
      this.channelClosedForBfcache = false;
    }
    // Se reevalúa siempre, pero el caso que importa es event.persisted ===
    // true: la página vuelve del bfcache con el heap de JS intacto (Signals
    // incluidos) sin que haya corrido un solo tick durante la suspensión
    // (D8). La llamada es síncrona — debe ocurrir antes del primer
    // repintado con datos de cuentas, no en el siguiente tick.
    this.reevaluate();
  };

  private registerForcedReevaluationListeners(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
  }

  private unregisterForcedReevaluationListeners(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  // ── BroadcastChannel — coordinación entre pestañas (§1.3) ────────────────

  private postMessage(type: SessionBroadcastMessage['type'], reason?: LogoutReason): void {
    const message: SessionBroadcastMessage = { type, correlationId: this.correlationId(), reason };
    this.channel.postMessage(message);
  }

  /** /login con o sin ?sessionExpired=1 según la causa real del cierre (§2.3). */
  private navigateToLogin(reason: LogoutReason): void {
    if (reason === 'inactivity') {
      this.router.navigate(['/login'], { queryParams: { sessionExpired: '1' } });
    } else {
      this.router.navigate(['/login']);
    }
  }

  private handleBroadcastMessage(msg: SessionBroadcastMessage | undefined): void {
    if (!msg?.type) return;

    if (msg.type === 'LOGOUT') {
      // LOGOUT se honra siempre, incluso si el correlationId no coincide.
      // Descartarlo por una discrepancia sería "fallar abriendo" — dejar
      // viva una sesión que debería estar cerrada — exactamente lo que la
      // regla del doble reloj y la regla crítica #13 prohíben. El riesgo de
      // ACTIVITY/WARNING_DISMISSED es el opuesto (prolongar indebidamente),
      // así que esos sí se validan estrictamente, abajo.
      this.terminateSession();
      this.navigateToLogin(msg.reason ?? 'manual');
      return;
    }

    if (msg.correlationId !== this.correlationId()) return; // mensaje de otra sesión — descartar
    if (this._state() === 'expired') return; // terminal — ningún ACTIVITY la resucita

    // ACTIVITY y WARNING_DISMISSED tienen el mismo efecto sobre esta pestaña:
    // la actividad ocurrió en OTRA pestaña de la misma sesión, así que se
    // trata como si hubiera ocurrido aquí. Se mantienen como tipos de mensaje
    // separados solo por claridad del protocolo (tabla de §1.3).
    this.setLastActivityNow();
    this.reevaluate();
  }

  // ── Cierre local ──────────────────────────────────────────────────────────

  private terminateSession(): void {
    // 1. Cortar el tráfico en curso — antes de cualquier otra cosa (D9, §1.5).
    this.logoutSubject.next();
    // 2. Estado terminal local (§1.3, regla #15).
    this._state.set('expired');
    this._secondsRemaining.set(0);
    this.stopTimer();
    // 3. Limpiar AuthService (closure de contraseña, Signals, permisos).
    //    Antes, esto solo ocurría en el logout manual (TopbarComponent llama
    //    a authService.logout() explícitamente); el auto-logout por
    //    inactividad y la recepción de LOGOUT en otras pestañas dejaban
    //    AuthService.isAuthenticated() en true pese a sessionStorage vacío.
    //    authService.logout() es seguro de llamar más de una vez.
    this.authService.logout();
    sessionStorage.clear();
  }

  // ── correlationId ─────────────────────────────────────────────────────────

  private loadOrGenerateCorrelationId(): string {
    try {
      const stored = sessionStorage.getItem(CORRELATION_STORAGE_KEY);
      if (stored) return stored;
    } catch {
      // sessionStorage inaccesible (modo privado estricto, etc.) — generar uno nuevo igual.
    }
    return this.generateCorrelationId();
  }

  private trySetCorrelationStorage(id: string): void {
    try {
      sessionStorage.setItem(CORRELATION_STORAGE_KEY, id);
    } catch {
      // No crítico: en el peor caso, el guard de correlationId entre
      // pestañas no reconoce a esta pestaña como parte de la misma sesión.
    }
  }

  /** Genera un UUID v4. Usa crypto.randomUUID() si está disponible, con fallback para entornos sin soporte. */
  private generateCorrelationId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.channel.close();
    this.logoutSubject.complete();
  }
}
